/**
 * Heart Reaction Component (Viber-style)
 * Compact toolbar button — click for instant heart, hover for quick reactions popup
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useToggleReaction } from '@/hooks/useReactions';
import EmojiPicker from './EmojiPicker';
import type { MessageReaction } from '@/types/api.types';

interface HeartReactionProps {
  messageId: string;
  reactions: MessageReaction[];
}

const QUICK_EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🔥'];

const HeartReaction = ({ messageId, reactions }: HeartReactionProps) => {
  const { user } = useAuthStore();
  const { mutate: toggleReaction, isPending } = useToggleReaction();

  const [showPopup, setShowPopup] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasHeartReaction = reactions.some(
    (r) => r.emoji === '❤️' && r.user_id === user?.id
  );

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const handleHeartClick = useCallback(() => {
    if (isPending) return;
    toggleReaction({ messageId, emoji: '❤️' });
    if (!hasHeartReaction) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [messageId, isPending, toggleReaction, hasHeartReaction]);

  const handleQuickReaction = useCallback(
    (emoji: string) => {
      if (isPending) return;
      toggleReaction({ messageId, emoji });
      setShowPopup(false);
      setShowFullPicker(false);
      if (emoji === '❤️' && !hasHeartReaction) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 300);
      }
    },
    [messageId, isPending, toggleReaction, hasHeartReaction]
  );

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      toggleReaction({ messageId, emoji });
      setShowFullPicker(false);
      setShowPopup(false);
    },
    [messageId, toggleReaction]
  );

  const cancelLeave = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    cancelLeave();
    hoverTimeoutRef.current = setTimeout(() => setShowPopup(true), 250);
  }, [cancelLeave]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (showFullPicker) return;
    leaveTimeoutRef.current = setTimeout(() => setShowPopup(false), 300);
  }, [showFullPicker]);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Heart button — same size as other toolbar buttons */}
      <button
        onClick={handleHeartClick}
        disabled={isPending}
        className={`p-2 rounded-lg transition-colors disabled:opacity-50
          ${hasHeartReaction
            ? 'text-red-500 hover:text-red-600'
            : 'text-gray-500 hover:text-red-500 dark:text-dark-text-muted dark:hover:text-red-400'
          }
          ${isAnimating ? 'animate-heart-pulse' : ''}
        `}
        title="Реакция ❤️"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill={hasHeartReaction ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>

      {/* Quick reactions popup — wrapped in a bridge div to prevent hover gaps */}
      {showPopup && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 pb-1 z-50"
          onMouseEnter={cancelLeave}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-dark-bg-elevated border border-gray-200 dark:border-dark-border-primary rounded-full shadow-lg animate-reaction-popup">
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleQuickReaction(emoji)}
                disabled={isPending}
                className="w-7 h-7 flex items-center justify-center text-base hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-full transition-transform hover:scale-125 disabled:opacity-50"
              >
                {emoji}
              </button>
            ))}

            <div className="relative">
              <button
                onClick={() => setShowFullPicker(!showFullPicker)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-dark-text-muted dark:hover:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-full transition-colors"
                title="Още емоджита"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>

              {showFullPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowFullPicker(false)}
                  placement="right"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeartReaction;
